/**
 * postMessage contract for the embedded event checkout (KTD3), shared by the
 * hosted checkout page (U2) and the RSVP client's embed mode (U3). The plain-JS
 * loader in `public/widget/school-journey-events.js` carries an identical copy
 * of these names — keep the two in lockstep.
 *
 * Namespaced `sj-checkout:*`. Outbound (iframe -> parent) posts always carry the
 * instance id (the event id) and use an EXPLICIT targetOrigin resolved from the
 * host page — never '*' unless the host origin is genuinely undeterminable.
 */
export const CHECKOUT_MSG = {
  // parent -> iframe
  INIT: 'sj-checkout:init',
  // iframe -> parent
  RENDER_COMPLETE: 'sj-checkout:renderComplete',
  HEIGHT_CHANGE: 'sj-checkout:heightChange',
  ORDER_COMPLETE: 'sj-checkout:orderComplete',
  SUBMIT_FAILED: 'sj-checkout:submitFailed',
  NOT_ELIGIBLE: 'sj-checkout:notEligible',
  MISCONFIGURED: 'sj-checkout:misconfigured',
  LOAD_FAILED: 'sj-checkout:loadFailed',
  CLOSE: 'sj-checkout:close'
} as const

export type CheckoutMessageType = (typeof CHECKOUT_MSG)[keyof typeof CHECKOUT_MSG]

/**
 * The host page's origin — the only place lifecycle messages are posted. Prefer
 * the `parent` param the loader threads into the iframe src; fall back to the
 * referrer; only as a last resort return '*' (the probe frames openly, but an
 * explicit origin is always preferred so an orderId never broadcasts widely).
 */
export function resolveHostOrigin(parent: string | null | undefined): string {
  const fromUrl = (value: string | null | undefined): string | null => {
    if (!value) return null
    try {
      const origin = new URL(value).origin
      return origin && origin !== 'null' ? origin : null
    } catch {
      return null
    }
  }
  return fromUrl(parent) ?? (typeof document !== 'undefined' ? fromUrl(document.referrer) : null) ?? '*'
}

/** Post a lifecycle message to the framing parent with an explicit targetOrigin. */
export function postToParent(
  type: CheckoutMessageType,
  instanceId: string,
  hostOrigin: string,
  payload?: Record<string, unknown>
): void {
  if (typeof window === 'undefined' || window.parent === window) return
  window.parent.postMessage({ type, instanceId, ...(payload ?? {}) }, hostOrigin)
}
